/**
 * Sidebar
 * Desktop (lg+) navigation column (Figma "Dashboard — returning user").
 * Presentational only: callers build the items, so the authenticated nav and
 * the guest (pre-login) nav share the same look without importing each other.
 * Fixed 240px column; layouts offset their content with lg:pl-60.
 */

import type { ElementType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import bquestLogo from '@/assets/bquest-logo.png';
import { Button } from '@/shared/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/components/ui/utils';

export type SidebarItem = {
  key: string;
  label: string;
  icon: ElementType;
  /** Link target; without it the item renders as a button */
  href?: string;
  onClick?: () => void;
  active?: boolean;
  /** Muted with a lock icon (guest-only destinations) */
  locked?: boolean;
  /** Tooltip text, e.g. why an item is locked */
  hint?: string;
  badge?: ReactNode;
};

type SidebarProps = {
  homeHref: string;
  /** Small uppercase label under the logo (e.g. the user's role) */
  label?: string;
  items: SidebarItem[];
  footerItems?: SidebarItem[];
  /** Primary call to action above the footer items */
  cta?: { label: string; href: string };
  profile?: { name: string; initials: string; photoUrl?: string | null; href: string; active: boolean };
};

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

const itemClass = (item: SidebarItem) =>
  cn(
    'flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] leading-[21px] transition-colors',
    focusRing,
    item.active
      ? 'bg-white/[0.14] font-semibold text-white'
      : item.locked
        ? 'font-medium text-white/40 hover:bg-white/5 hover:text-white/60'
        : 'font-medium text-[#D7E2DC] hover:bg-white/10 hover:text-white'
  );

const SidebarEntry = ({ item }: { item: SidebarItem }) => {
  const Icon = item.icon;
  const content = (
    <>
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
      <span>{item.label}</span>
      {item.locked && <Lock className="ml-auto h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {item.badge}
    </>
  );
  const entry = item.href ? (
    <Link
      to={item.href}
      onClick={item.onClick}
      aria-current={item.active ? 'page' : undefined}
      className={itemClass(item)}
    >
      {content}
    </Link>
  ) : (
    <button type="button" onClick={item.onClick} className={itemClass(item)}>
      {content}
    </button>
  );

  if (!item.hint) return entry;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{entry}</TooltipTrigger>
      <TooltipContent side="right">{item.hint}</TooltipContent>
    </Tooltip>
  );
};

export const Sidebar = ({ homeHref, label, items, footerItems = [], cta, profile }: SidebarProps) => (
  <TooltipProvider delayDuration={200}>
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col overflow-y-auto bg-[#1F4B45] px-4 py-6 lg:flex">
      <div className="px-3 pb-3">
        <Link to={homeHref} className="inline-block transition-opacity hover:opacity-80">
          <img src={bquestLogo} alt="bQuest" className="h-6 brightness-0 invert" />
        </Link>
      </div>

      {label && (
        <p className="px-3 pb-5 text-[13px] font-semibold uppercase leading-[19px] tracking-[0.78px] text-[#9DB7AC]">
          {label}
        </p>
      )}

      {items.length > 0 && (
        <nav aria-label="Main navigation" className="flex flex-col gap-0.5">
          {items.map((item) => (
            <SidebarEntry key={item.key} item={item} />
          ))}
        </nav>
      )}

      <div className="flex-1" />

      {cta && (
        <Button asChild className="mx-3 mb-2 bg-white text-[#1F4B45] hover:bg-white/90 font-bold">
          <Link to={cta.href}>{cta.label}</Link>
        </Button>
      )}

      {footerItems.map((item) => (
        <SidebarEntry key={item.key} item={item} />
      ))}

      {profile && (
        <>
          <div className="p-3">
            <div className="h-px bg-white/20" />
          </div>

          <Link
            to={profile.href}
            aria-current={profile.active ? 'page' : undefined}
            className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-1.5 transition-colors hover:bg-white/10', focusRing)}
          >
            <Avatar className="h-9 w-9">
              {profile.photoUrl && <AvatarImage src={profile.photoUrl} alt="" />}
              <AvatarFallback className="bg-[#D9A83E] text-sm font-bold text-[#2A2012]">
                {profile.initials}
              </AvatarFallback>
            </Avatar>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold text-white">{profile.name}</span>
              <span className="text-xs text-[#9DB7AC]">View profile</span>
            </span>
          </Link>
        </>
      )}
    </aside>
  </TooltipProvider>
);
